// [목적] production 빌드에서만 Service Worker(public/sw.js) 를 등록하고, 새 버전 감지 시
//        "새로고침" 유도 배너를 렌더한다.
// [이유] G1-5(PWA): main.tsx 가 DEV 에서는 MSW 와 충돌 가능성이 있어 등록하지 않도록 요구됨
//        (CLAUDE 위임 카드 "DEV·MSW 와 충돌 금지"). registration.waiting 이 이미 있거나 새로 감지되면
//        사용자가 명시적으로 새로고침을 눌러야 skipWaiting → controllerchange → reload 로 전환한다
//        (자동 리로드는 작업 중인 폼 등을 날릴 수 있어 사용자 동의 없이 하지 않음).
// [사이드 이팩트] navigator.serviceWorker.register('/sw.js') 1회 호출(프로덕션 전용) — 등록 실패는 조용히 무시
//                (PWA 는 향상 기능이지 필수 기능이 아니므로 앱 렌더를 막지 않는다).
// [수정 시 고려사항] sw.js 경로/캐시 버전 변경 시 이 파일은 수정 불필요(등록 로직만 담당) — 캐싱 정책은
//                   public/sw.js 단일 소스.

import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

const c = tokens.color;
const L = 'light' as const;

/**
 * [목적] Service Worker 등록 + 업데이트 감지 상태를 관리하고, 감지 시 하단 배너를 렌더
 * [이유] G1-5: "새 버전 감지 시 업데이트 알림 UX" 요구사항 충족.
 * [사이드 이팩트] production && serviceWorker 지원 브라우저에서만 동작 — 그 외 환경에서는 아무것도 렌더 안 함.
 * [수정 시 고려사항] StrictMode 이중 실행에도 안전 — register() 는 멱등(이미 등록된 스크립트면 기존 등록 반환).
 */
export function PwaUpdateNotifier(): JSX.Element | null {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  // [목적] 사용자가 "새로고침" 버튼을 눌러 우리가 직접 트리거한 리로드인지 구분하는 플래그
  // [이유] 버그 발견(G2-4 E2E 디버깅 중): sw.js activate 핸들러가 self.clients.claim() 을 호출하면
  //        "최초 방문"에도(이전 활성 SW 가 전혀 없던 상태에서 새 SW 가 즉시 activate) controllerchange 가
  //        발생한다 — 이 경우까지 무조건 reload() 하면 방문자가 사이트 진입 직후 원인 불명의 자동 새로고침을
  //        겪는 실서비스 버그가 된다(구글 PWA 문서에 문서화된 잘 알려진 함정). ref 이므로 리렌더와 무관하게
  //        handleReload 호출 시점의 최신 값을 controllerchange 핸들러(별도 useEffect, 마운트 시 1회 등록)가
  //        참조할 수 있다.
  // [사이드 이팩트] 없음(상태 플래그).
  // [수정 시 고려사항] 이 플래그가 true 로 설정되는 유일한 경로는 handleReload() — 다른 경로로 true 가 되면
  //                   똑같은 오탐 재발 가능.
  const userInitiatedReloadRef = useRef(false);

  useEffect(() => {
    // [사이드 이팩트] DEV 환경(MSW 병행 가능)에서는 등록하지 않음 — production 빌드 전용.
    if (!import.meta.env.PROD) return;
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;

    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        if (cancelled) return;
        setRegistration(reg);

        // 이미 대기 중인 새 SW 가 있으면(예: 등록 직후 재방문) 즉시 배너 표시
        if (reg.waiting && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // controller 가 이미 있다(=이전에 활성 SW 가 있었다) + 새 SW 가 installed 상태 → 업데이트 상황
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
            }
          });
        });
      })
      .catch(() => {
        // ponytail: 등록 실패는 조용히 무시(PWA 는 향상 기능) — 콘솔 경고만 남김
        console.warn('[PWA] service worker 등록 실패');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // [수정 시 고려사항] 등록 useEffect 와 동일 가드 — PROD 가 아니면 애초에 register() 를 호출하지
    //                   않으므로 controllerchange 리스너도 불필요(등록 안 함).
    if (!import.meta.env.PROD) return;
    if (!('serviceWorker' in navigator)) return;
    let reloaded = false;
    const onControllerChange = (): void => {
      if (reloaded) return;
      // [목적] 사용자가 직접 "새로고침"을 누른 경우에만 reload — 그 외(예: 최초 방문 시 clients.claim()으로
      //        인한 controllerchange)는 무시한다(위 userInitiatedReloadRef 주석 근거).
      if (!userInitiatedReloadRef.current) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () =>
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  const handleReload = (): void => {
    userInitiatedReloadRef.current = true;
    registration?.waiting?.postMessage('SKIP_WAITING');
  };

  if (!updateAvailable) return null;

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: `${tokens.spacing[4]}px`,
        transform: 'translateX(-50%)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: `${tokens.spacing[3]}px`,
        bgcolor: c.ink.high[L],
        color: c.paper[0][L],
        borderRadius: `${tokens.radius.l}px`,
        boxShadow: tokens.shadow.modal,
        px: `${tokens.spacing[4]}px`,
        py: `${tokens.spacing[2]}px`,
      }}
    >
      <Typography sx={{ fontSize: 13 }}>새 버전이 있어요.</Typography>
      <Button
        onClick={handleReload}
        size="small"
        variant="contained"
        sx={{
          bgcolor: c.brand[500][L],
          textTransform: 'none',
          borderRadius: `${tokens.radius.m}px`,
          '&:hover': { bgcolor: c.brand[500][L] },
          '&:focus-visible': { outline: `2px solid ${c.paper[0][L]}`, outlineOffset: 2 },
        }}
      >
        새로고침
      </Button>
    </Box>
  );
}
